import { useMemo, useState } from 'react';

import { useAppContext } from '../../../../../app/Context';
import { notEmpty } from '../../../../../utils/array';
import { useClassContext } from '../../../../class/Context';
import { usePollContext } from '../../../Context';
import { ErrorsListProps } from '../../ErrorsList';
import { getCanPublishPoll, getIsPollPublishable } from '../../helpers';
import { getPublishAlertMessage } from './helpers';
import { i18nNS } from '../../../../../i18n';
import { useTranslation } from 'react-i18next';

export interface UsePublishPoll extends Pick<ErrorsListProps, 'errors'> {
  triggerValidations: () => Promise<boolean>;
  isClassClosed: boolean;
}

const usePublishPoll = ({ triggerValidations, errors, isClassClosed }: UsePublishPoll) => {
  const { t } = useTranslation([i18nNS.POLL]);

  const { isPresenting } = useAppContext();

  const { cls } = useClassContext();
  const { poll } = usePollContext();

  const pollQuestions = Object.values(poll?.questionsById ?? {}).filter(notEmpty);

  const canPublish = getCanPublishPoll(poll);

  const tooltipTitle = useMemo(() => {
    if (isClassClosed) {
      return t('this_activity_cannot_be_published_outside_the_scheduled_class_end_time', { ns: i18nNS.POLL });
    }

    return t('publish_poll', { ns: i18nNS.POLL });
  }, [isClassClosed]);

  const alertBody = getPublishAlertMessage(cls, poll, pollQuestions.length);

  const hasErrors = useMemo(() => {
    if (!isPresenting) return false;

    if (!errors) return false;

    if (Object.keys(errors).length === 0) return false;

    return Object.values(errors).some((error) => {
      if (typeof error === 'string') {
        return Boolean(error);
      }

      if (typeof error === 'object' && Array.isArray(error)) {
        return error.reduce((acc, item) => {
          if (item) return true;
          return acc;
        }, false);
      }

      if (typeof error === 'object') {
        return Object.values(error).reduce((acc, item) => {
          if (item) return true;
          return acc;
        }, false);
      }

      return false;
    });
  }, [isPresenting, errors]);

  const [canOpenAlert, setCanOpenAlert] = useState(false);
  const [isPublishPollDrawerOpen, setIsPublishPollDrawerOpen] = useState(false);

  const handleClosePublishPollDrawer = () => {
    setIsPublishPollDrawerOpen(false);
  };

  const onPublishButtonClick = async () => {
    const isValid = await triggerValidations();
    if (!isValid) {
      if (!isPresenting) return;
      setCanOpenAlert(true);
      return;
    }

    const isPollPublishable = getIsPollPublishable(poll, cls?.startTime, cls?.endTime);
    if (!isPollPublishable || pollQuestions.length === 0) {
      setCanOpenAlert(true);
      return;
    }

    setCanOpenAlert(false);
    setIsPublishPollDrawerOpen(true);
  };

  const handlePublishButtonClick = () => {
    onPublishButtonClick();
  };

  const handleCloseAlert = () => {
    setCanOpenAlert(false);
  };

  return {
    canPublish,
    tooltipTitle,
    handlePublishButtonClick,
    hasErrors,
    canOpenAlert,
    alertBody,
    isPublishPollDrawerOpen,
    handleClosePublishPollDrawer,
    handleCloseAlert,
  };
};

export default usePublishPoll;
